import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentCard } from '../components/ContentCard';
import GeneratedArticle from '../components/GeneratedArticle';
import { fetchWithAuth } from '../api/client';
import { getAuth } from 'firebase/auth';
import SearchResultModal from '../components/SearchResultModal';

function ExplorePage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('feed');
  const [inputText, setInputText] = useState('');
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [content, setContent] = useState([]); 
  const [contentLoading, setContentLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    players: [],
    teams: [],
  });
  const [selectedResult, setSelectedResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ml/agent/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuth().currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          user_query: inputText,
          input_language: i18n.language
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate article');
      }

      setGeneratedArticle(data.final_response);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPosts = useCallback(async () => {
    setContentLoading(true);
    try {
      const posts = await fetchWithAuth('/api/ml/posts/all');
      const sortedPosts = posts.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      const processedPosts = sortedPosts.map(post => {
        if (!post[i18n.language] && post['en']) {
          return {
            ...post,
            [i18n.language]: post['en']
          };
        }
        return post;
      });
      
      setContent(processedPosts);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err.message);
    } finally {
      setContentLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults({ players: [], teams: [] });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/posts/search?query=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Error searching:', err);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'feed') {
      fetchPosts();
    }
  };

  const renderSearchResults = () => {
    if (!searchQuery.trim()) return null;

    return (
      <div className="space-y-6">
        {searchResults.players.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Players</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.players.map(player => (
                <div 
                  key={player.id} 
                  className="bg-dark-800 p-4 rounded-lg border border-dark-700 cursor-pointer hover:border-blue-500 transition-colors duration-200"
                  onClick={() => setSelectedResult({ id: player.id, type: 'player' })}
                >
                  <div className="flex items-center space-x-3">
                    {player.headshot_url && (
                      <img 
                        src={player.headshot_url} 
                        alt={player.mlb_person_fullName}
                        className="w-12 h-12 rounded-full"
                      />
                    )}
                    <div>
                      <h4 className="font-medium">{player.mlb_person_fullName}</h4>
                      <p className="text-sm text-gray-400">{player.mlb_position_name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {searchResults.teams.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Teams</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.teams.map(team => (
                <div 
                  key={team.id} 
                  className="bg-dark-800 p-4 rounded-lg border border-dark-700 cursor-pointer hover:border-blue-500 transition-colors duration-200"
                  onClick={() => setSelectedResult({ id: team.id, type: 'team' })}
                >
                  <div className="flex items-center space-x-3">
                    {team.logo_url && (
                      <img 
                        src={team.logo_url} 
                        alt={team.mlb_name}
                        className="w-12 h-12"
                      />
                    )}
                    <div>
                      <h4 className="font-medium">{team.mlb_name}</h4>
                      <p className="text-sm text-gray-400">{team.mlb_locationName}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedResult && (
          <SearchResultModal 
            {...selectedResult}
            onClose={() => setSelectedResult(null)}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'explore') {
      return (
        <div className="space-y-6">
          <div className="max-w-2xl mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search')}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {renderSearchResults()}
        </div>
      );
    }

    if (activeTab === 'agent') {
      return (
        <div className="max-w-2xl mx-auto mt-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="w-full h-40 p-4 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('agentPlaceholder')}
              disabled={isLoading}
            />
            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}
            <button
              type="submit"
              className={`w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? t('loading') : t('createArticle')}
            </button>
          </form>

          {generatedArticle && (
            <GeneratedArticle 
              article={generatedArticle}
              onLanguageChange={(lang) => i18n.changeLanguage(lang)}
            />
          )}
        </div>
      );
    }

    // Feed tab content
    return (
      <>
        {contentLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : content.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            {t('noResults')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-lg border border-dark-700 p-1 bg-dark-800">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === 'feed'
                ? 'bg-blue-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => handleTabChange('feed')}
          >
            {t('feed')}
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === 'explore'
                ? 'bg-blue-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => handleTabChange('explore')}
          >
            {t('explore')}
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === 'agent'
                ? 'bg-blue-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => handleTabChange('agent')}
          >
            ✨ {t('agent')}
          </button>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}

export default ExplorePage;
